<template>
  <div class="update-pwd-container">
    <!-- 动态背景装饰 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <!-- 居中重置密码区域 -->
    <div class="update-pwd-wrapper">
      <div class="update-pwd-box">
        <div class="logo-wrapper">
          <!-- <img src="@/assets/logo.png" alt="Logo" class="logo" /> -->
        </div>
        
        <div class="update-pwd-header">
          <h2 class="update-pwd-title">重置密码</h2>
          <p class="update-pwd-subtitle">请输入您的手机号和验证码</p>
        </div>
        
        <el-form :model="updatePwdForm" :rules="rules" ref="updatePwdFormRef" class="update-pwd-form">
          <el-form-item prop="phone">
            <div class="input-wrapper">
              <label class="input-label">手机号</label>
              <el-input
                v-model="updatePwdForm.phone"
                placeholder="请输入手机号"
                size="large"
                maxlength="11"
                clearable
              />
            </div>
          </el-form-item>
          
          <el-form-item prop="code">
            <div class="input-wrapper">
              <label class="input-label">验证码</label>
              <div class="code-input-wrapper">
                <el-input
                  v-model="updatePwdForm.code"
                  placeholder="请输入验证码"
                  size="large"
                  clearable
                />
                <el-button 
                  class="code-btn" 
                  size="large"
                  :disabled="countdown > 0"
                  @click="sendCode"
                >
                  {{ countdown > 0 ? `${countdown}秒后重试` : '获取验证码' }}
                </el-button>
              </div>
            </div>
          </el-form-item>

          <el-form-item prop="newPassword">
            <div class="input-wrapper">
              <label class="input-label">新密码</label>
              <el-input
                v-model="updatePwdForm.newPassword"
                type="password"
                placeholder="请输入新密码"
                size="large"
                show-password
              />
            </div>
          </el-form-item>

          <el-form-item prop="confirmPassword">
            <div class="input-wrapper">
              <label class="input-label">确认密码</label>
              <el-input
                v-model="updatePwdForm.confirmPassword"
                type="password"
                placeholder="请再次输入新密码"
                size="large"
                show-password
              />
            </div>
          </el-form-item>

          <el-form-item>
            <el-button 
              type="primary" 
              size="large" 
              class="update-pwd-btn" 
              @click="handleUpdatePwd"
              :loading="loading"
            >
              {{ loading ? '重置中...' : '确认重置' }}
            </el-button>
          </el-form-item>

          <div class="back-to-login">
            <a href="#" @click.prevent="backToLogin">返回登录</a>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UpdatePwd'
}
</script>

<script setup>
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { sendSmsCode, forgotPassword } from '@/api/auth'

const router = useRouter()
const updatePwdFormRef = ref(null)
const loading = ref(false)
const countdown = ref(0)

const updatePwdForm = reactive({
  phone: '',
  code: '',
  newPassword: '',
  confirmPassword: ''
})

const validateConfirmPassword = (rule, value, callback) => {
  if (value === '') {
    callback(new Error('请再次输入密码'))
  } else if (value !== updatePwdForm.newPassword) {
    callback(new Error('两次输入密码不一致'))
  } else {
    callback()
  }
}

const rules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
  ],
  code: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '请输入6位数字验证码', trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, validator: validateConfirmPassword, trigger: 'blur' }
  ]
}

const sendCode = async () => {
  if (!updatePwdForm.phone) {
    ElMessage.warning('请先输入手机号')
    return
  }
  if (!/^1[3-9]\d{9}$/.test(updatePwdForm.phone)) {
    ElMessage.warning('请输入正确的手机号')
    return
  }

  try {
    await sendSmsCode({ phoneNumber: updatePwdForm.phone })
    ElMessage.success('验证码已发送')
    countdown.value = 60
    const timer = setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) {
        clearInterval(timer)
      }
    }, 1000)
  } catch (error) {
    // 错误提示已在请求拦截器中统一处理
    console.error('发送验证码失败：', error)
  }
}

const handleUpdatePwd = () => {
  updatePwdFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        await forgotPassword({
          phoneNumber: updatePwdForm.phone,
          verificationCode: updatePwdForm.code,
          newPassword: updatePwdForm.newPassword,
          confirmNewPassword: updatePwdForm.confirmPassword
        })
        ElMessage.success('密码重置成功，请重新登录')
        setTimeout(() => {
          router.push('/login')
        }, 1500)
      } catch (error) {
        // 错误提示已在请求拦截器中统一处理
        console.error('重置密码失败：', error)
      } finally {
        loading.value = false
      }
    }
  })
}

const backToLogin = () => {
  router.push('/login')
}
</script>

<style scoped>
.update-pwd-container {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%);
}

/* 动态背景装饰 */
.bg-decoration {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 20s infinite ease-in-out;
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  left: -100px;
  animation-delay: 0s;
}

.circle-2 {
  width: 400px;
  height: 400px;
  bottom: -150px;
  right: -150px;
  animation-delay: 5s;
}

.circle-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 30%;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

/* 居中重置密码区域 */
.update-pwd-wrapper {
  position: relative;
  z-index: 1;
  animation: fadeInUp 0.8s ease-out;
}

.update-pwd-box {
  width: min(440px, 90vw);
  padding: min(45px, 4vh) min(40px, 5vw);
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.logo-wrapper {
  text-align: center;
  margin-bottom: min(20px, 2vh);
  animation: fadeInDown 1s ease-out;
}

.logo {
  width: min(70px, 10vw);
  height: min(70px, 10vw);
  object-fit: contain;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15));
}

.update-pwd-header {
  text-align: center;
  margin-bottom: min(35px, 3vh);
}

.update-pwd-title {
  font-size: clamp(20px, 2.5vw, 24px);
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.update-pwd-subtitle {
  font-size: clamp(12px, 1.5vw, 14px);
  color: #666;
  font-weight: 400;
}

.update-pwd-form {
  margin-top: min(25px, 2.5vh);
}

.input-wrapper {
  margin-bottom: 4px;
  width: 100%;
}

.input-label {
  display: block;
  font-size: clamp(12px, 1.5vw, 14px);
  font-weight: 500;
  color: #333;
  margin-bottom: min(8px, 1vh);
}

.code-input-wrapper {
  display: flex;
  gap: 10px;
}

.code-input-wrapper :deep(.el-input) {
  flex: 1;
}

.code-btn {
  width: min(120px, 25vw);
  flex-shrink: 0;
  font-size: clamp(12px, 1.5vw, 14px);
}

:deep(.el-form-item) {
  margin-bottom: min(20px, 2vh);
}

:deep(.el-form-item__content) {
  width: 100%;
}

:deep(.el-input) {
  width: 100%;
  display: block;
}

:deep(.el-input__wrapper) {
  width: 100%;
  display: flex;
  padding: min(12px, 1.2vh) min(16px, 1.5vw);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.25);
}

:deep(.el-input__inner) {
  font-size: clamp(13px, 1.5vw, 15px);
}

.update-pwd-btn {
  width: 100%;
  height: min(48px, 5vh);
  font-size: clamp(14px, 1.8vw, 16px);
  font-weight: 600;
  border-radius: 12px;
  background: #409eff;
  border: none;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.3);
  margin-top: 10px;
}

.update-pwd-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.4);
  background: #66b1ff;
}

.update-pwd-btn:active {
  transform: translateY(0);
}

.back-to-login {
  text-align: center;
  margin-top: min(20px, 2vh);
}

.back-to-login a {
  color: #409eff;
  text-decoration: none;
  font-size: clamp(12px, 1.5vw, 14px);
  font-weight: 500;
  transition: color 0.3s;
}

.back-to-login a:hover {
  color: #66b1ff;
}

/* 动画 */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .update-pwd-box {
    width: 90%;
    padding: 40px 30px;
  }
  
  .update-pwd-title {
    font-size: 20px;
  }
  
  .logo {
    width: 60px;
    height: 60px;
  }

  .code-btn {
    width: 100px;
    font-size: 13px;
  }
}
</style>
